/**
 * LandingPage (`/`) — Bayenah V5 marketing surface.
 *
 * Sections (in order):
 *   1. Nav            — bilingual brand lockup + EN/AR toggle + "Request a Demo"
 *   2. Hero           — full-bleed background + interactive globe (right)
 *   3. Truth          — "Your best lawyers are doing the wrong work"
 *   4. Engine         — seven agent grid, Harvey-grade monochrome
 *   5. Features       — bento (Assistant, Review, Drafting, Knowledge Base, Herald)
 *   6. Compare        — General AI vs Bayenah feature table
 *   7. Security       — three deployment options + trust signals row
 *   8. Final CTA      — Request a Demo / Talk to us
 *   9. Footer         — bilingual mark + Platform/Security/Trust/Privacy/Terms
 *
 * Bilingual via useLanguage() — every string passes through the COPY[language] map.
 * No mixed-language rendering; the entire surface flips EN <-> AR on toggle.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import InteractiveGlobe from '@/components/ui/InteractiveGlobe';

// ── Bilingual copy dictionary (scoped to this page) ─────────────────
const COPY = {
  en: {
    dir: 'ltr',
    lang: 'en',
    nav: {
      tagline: 'Saudi Legal AI',
      platform: 'Platform',
      security: 'Security',
      trust: 'Trust',
      signin: 'Sign in',
      requestDemo: 'Give it a try',
    },
    hero: {
      eyebrow: 'Saudi Legal AI',
      headline: ['Legal work,', 'perfected.'],
      sub: 'The only legal AI built exclusively on Saudi law. Seven specialized agents work simultaneously so your team focuses on judgment, not drafts.',
      ctaPrimary: 'Give it a try',
      ctaSecondary: 'See how it works ↓',
      ctaFullAccess: 'Request full access',
      globeHint: 'Interactive — drag to rotate',
    },
    truth: {
      eyebrow: 'The reality',
      headline: 'Your best lawyers are doing the wrong work.',
      body: 'They trained for years to think. You are paying them to type. A senior lawyer spending three days on a first draft is not a legal problem — it is an operational one. Bayenah solves it.',
      cards: [
        { label: 'Speed',        title: 'Days compressed into minutes.',                  body: 'From first draft to cited, structured contract — faster than any review cycle you currently run. Every time.' },
        { label: 'Coverage',     title: 'Every Saudi law and regulation. Every article.', body: 'Thousands of indexed articles across Saudi regulatory frameworks — Civil Transactions, Companies Law, PDPL, Arbitration, Anti-Bribery, Labor Law, and more.' },
        { label: 'Architecture', title: 'Seven agents. Simultaneously.',                    body: 'Not one model doing everything. Seven specialized agents — each with a distinct legal function — working in parallel on every single task.' },
      ],
    },
    engine: {
      eyebrow: 'The architecture',
      headline: 'Not a chatbot. An engine.',
      sub: 'Bayenah orchestrates seven specialized agents — each built for a distinct legal role. They work the way a real legal team operates: simultaneously, not sequentially.',
      closing: 'While Scribe drafts, Examiner and Regis audit simultaneously. Conductor waits for both. Your team waits for none.',
      agents: [
        { name: 'Scribe',     role: 'Drafts contracts',             desc: "Deploys five parallel sub-agents — each drafting a different section simultaneously. Foundation, obligations, IP, termination, governance — all at once. Your firm's templates first. Saudi law always." },
        { name: 'Examiner',   role: 'Reviews for legal risk',       desc: 'Scores every contract across ten legal dimensions. Flags weak obligations, missing protections, ambiguous terms. Every finding cited to a specific Saudi law article.' },
        { name: 'Regis',      role: 'Audits regulatory compliance', desc: 'Stress-tests against Saudi regulatory frameworks simultaneously. Returns a structured compliance report per framework — not a generic summary.' },
        { name: 'Conductor',  role: 'Synthesizes all findings',     desc: 'Merges Examiner risk findings and Regis compliance gaps into one unified prioritized report. No finding lost. No contradiction unresolved. One output.' },
        { name: 'Sentinel',   role: 'Guards your Knowledge Base',   desc: 'Reads every document your team uploads. Categorizes, summarizes, monitors. Detects conflicts between your own policies before they cause governance incidents.' },
        { name: 'Herald',     role: 'Routes legal intelligence',    desc: 'Composes complete context-rich briefings from a raw note. Routes them to the right person — contract, findings, and suggested action attached. In-app and by email.' },
        { name: 'Counsel',    role: 'Researches Saudi law',           desc: 'Answers questions against the actual text of the regulation — statutes and current regulation, cited to the article rather than paraphrased from a summary.' },
      ],
    },
    features: {
      eyebrow: 'The platform',
      headline: ['Built for how Saudi legal', 'work actually happens.'],
      cards: {
        assistant: {
          eyebrow: 'Legal research',
          title: 'Smart Legal Assistant',
          desc: 'Ask any question about Saudi law. Get an answer grounded in the actual text of the regulation — cited to the article, not paraphrased from a summary. In Arabic or English.',
          demoQuestion: 'How many work hours per week under Saudi Labor Law?',
          demoAnswer: 'Per Article 98 of the Labor Law, an employee may not be required to work more than eight hours per day or forty-eight hours per week.',
          demoArticle: 'Article 98',
          demoSourcesPill: '6 legal sources used',
        },
        review: {
          eyebrow: 'Analysis',
          title: 'Contract Review & Risk Analysis',
          desc: 'Upload any contract. Four agents review simultaneously. One prioritized report.',
          risks: [
            { label: 'Critical', text: 'Missing arbitration clause' },
            { label: 'Medium',   text: 'Notice period below minimum' },
            { label: 'Low',      text: 'Confidentiality clause present' },
          ],
        },
        drafting: {
          eyebrow: 'Drafting',
          title: 'Contract Drafting',
          desc: 'Describe in plain Arabic or English. Five sections drafted simultaneously. Complete in minutes.',
          previewTitle: 'MUTUAL NON-DISCLOSURE AGREEMENT',
          articles: ['Article 1. Definitions', 'Article 2. Confidentiality Obligations', 'Article 3. Permitted Disclosures'],
          more: '+ 6 more articles…',
        },
        kb: {
          eyebrow: 'Intelligence',
          title: 'Knowledge Base',
          desc: "Your firm's policies and templates — uploaded once, active everywhere. Sentinel monitors everything automatically.",
          docs: [
            { name: 'HR Policy 2025',       status: 'Active' },
            { name: 'Authority Matrix v3',  status: 'Active' },
            { name: 'Partnership Template', status: 'Active' },
          ],
        },
        herald: {
          eyebrow: 'Collaboration',
          title: 'Herald',
          desc: 'Type a note. Herald composes a complete legal briefing and routes it to the right colleague with everything attached.',
          demoLabel: 'To',
          demoInput: '@Ahmed — not sure about clause 5',
          demoAttachment: 'Partnership Agreement attached',
          demoPreview: 'Preview message →',
        },
        library: {
          eyebrow: 'Output archive',
          title: 'Library',
          desc: "Every contract Bayenah has ever produced for your team — stored, searchable, and ready to reuse. Download as DOCX, PDF, or text. Review again with one click. Your institutional knowledge never walks out the door.",
          entries: [
            { name: 'Partnership Agreement — Aramco.pdf', status: 'Drafted',  date: 'Jan 2026' },
            { name: 'NDA — Vendor Review.pdf',            status: 'Reviewed', date: 'Jan 2026' },
            { name: 'Employment Contract.docx',            status: 'Drafted',  date: 'Dec 2025' },
          ],
          download: 'Download',
        },
      },
    },
    compare: {
      eyebrow: 'Why not general AI',
      headline: ['Generic AI guesses Saudi law.', 'Bayenah knows it.'],
      colGen: 'General AI',
      colBay: 'Bayenah',
      rows: [
        { feature: 'Legal knowledge',         gen: '✗  Approximated from global data',     bay: '✓  Saudi law — article-level citations' },
        { feature: "Your firm's templates",   gen: '✗  None',                              bay: '✓  Library feeds every draft' },
        { feature: 'Processing',              gen: '✗  One model, sequential',             bay: '✓  Seven agents, parallel' },
        { feature: 'Data sovereignty',        gen: '✗  Contracts leave your organization', bay: '✓  Stays in your infrastructure' },
        { feature: 'Team collaboration',      gen: '✗  None',                              bay: '✓  Herald — @mention and route' },
        { feature: 'Governance intelligence', gen: '✗  None',                              bay: '✓  Sentinel Guardian' },
        { feature: 'Language',                gen: '✗  English-first, Arabic translated',  bay: '✓  Arabic-native, English-equal' },
      ],
      quote: '“ChatGPT is a generalist. Bayenah is a specialist. You would not ask a general practitioner to perform surgery.”',
    },
    security: {
      eyebrow: 'Security',
      headline: 'Your data never leaves your control.',
      sub: 'For legal teams handling sensitive negotiations, M&A transactions, and confidential corporate matters — three deployment options.',
      options: [
        { tag: 'Standard',   label: 'Option A', title: 'Bayenah Cloud',       body: 'Data processed and stored in-region. PDPL-compliant by architecture — not by policy.' },
        { tag: 'Enterprise', label: 'Option B', title: 'Amazon Bedrock',      body: 'Bayenah runs entirely inside your own AWS environment. Zero data visibility to Bayenah. Full sovereignty.' },
        { tag: 'Government', label: 'Option C', title: 'Private Deployment', body: 'On-premise or private cloud for government-adjacent entities and classified environments.' },
      ],
      trust: [
        'Role-based access control',
        'Full audit logs — every action',
        'Encrypted at rest and in transit',
        'Never used to train AI models',
      ],
    },
    cta: {
      headline: ['Your legal team is ready.', 'Bayenah is ready.'],
      body: 'The question is not whether your team needs this. The question is how much longer you can afford to wait.',
      ctaPrimary: 'Give it a try',
      ctaFullAccess: 'Request full access',
      ctaSecondary: 'Talk to us',
      fineprint: 'No commitment required.',
    },
    footer: {
      tagline: 'Legal AI for Saudi enterprise.',
      platform: 'Platform',
      security: 'Security',
      trust: 'Trust',
      privacy: 'Privacy',
      terms: 'Terms',
      copyright: `© ${new Date().getFullYear()} Bayenah. All rights reserved.`,
    },
  },

  ar: {
    dir: 'rtl',
    lang: 'ar',
    nav: {
      tagline: 'الذكاء القانوني السعودي',
      platform: 'المنصة',
      security: 'الأمان',
      trust: 'الثقة',
      signin: 'تسجيل الدخول',
      requestDemo: 'جرب مجانا',
    },
    hero: {
      eyebrow: 'ذكاء قانوني سعودي',
      headline: ['العمل القانوني،', 'في أذكى صوره.'],
      sub: 'أول منصة ذكاء قانوني مصممة خصيصًا على الأنظمة السعودية. سبعة وكلاء متخصصون يعملون بالتوازي، حتى يركز فريقك القانوني على القرار والرأي، لا على الصياغة والتجميع.',
      ctaPrimary: 'جرب مجانا',
      ctaSecondary: 'شاهد كيف تعمل ↓',
      ctaFullAccess: 'طلب الوصول الكامل',
      globeHint: 'تفاعلي — اسحب للتدوير',
    },
    truth: {
      eyebrow: 'الواقع',
      headline: 'أفضل محاميك يقضون وقتهم في العمل الخطأ.',
      body: 'تم تدريبهم لسنوات كي يفكروا، يحللوا، ويحكموا. لكنك تدفع لهم اليوم ليكتبوا المسودات، ويراجعوا البنود، ويعيدوا ترتيب النصوص. عندما يقضي محامٍ خبير ثلاثة أيام في إعداد مسودة أولى، فهذه ليست مشكلة قانونية. هذه مشكلة تشغيلية. Bayenah تعالجها من أساسها.',
      cards: [
        { label: 'السرعة',  title: 'أيام تُختصر إلى دقائق.',                body: 'من المسودة الأولى إلى عقد منظم ومدعوم بالإحالات النظامية، خلال وقت أقصر من أي دورة مراجعة تقليدية تعمل بها اليوم.' },
        { label: 'الشمول',  title: 'الأنظمة السعودية. المواد. التفاصيل.',  body: 'آلاف المواد النظامية المفهرسة ضمن الأطر التنظيمية السعودية، من نظام المعاملات المدنية، ونظام الشركات، وحماية البيانات الشخصية، والتحكيم، ومكافحة الرشوة، ونظام العمل، وغيرها.' },
        { label: 'البنية',  title: 'سبعة وكلاء. يعملون في وقت واحد.',         body: 'ليست منصة تعتمد على نموذج واحد يحاول فعل كل شيء. Bayenah تعمل من خلال سبعة وكلاء متخصصين، لكل واحد منهم دور قانوني محدد، يعملون بالتوازي على كل مهمة.' },
      ],
    },
    engine: {
      eyebrow: 'البنية',
      headline: 'ليست روبوت محادثة. إنها محرك قانوني.',
      sub: 'تنظم Bayenah عمل سبعة وكلاء متخصصين، كل وكيل مصمم لدور قانوني واضح. تعمل المنصة كما يعمل الفريق القانوني الحقيقي: بالتوازي، لا بالتسلسل.',
      closing: 'بينما يقوم Scribe بالصياغة، يراجع Examiner المخاطر ويدقق Regis الالتزام في نفس الوقت. ينتظر Conductor النتائج ليجمعها. أما فريقك، فلا ينتظر.',
      agents: [
        { name: 'Scribe',    role: 'يصيغ العقود',           desc: 'يشغّل خمسة وكلاء فرعيين بالتوازي، كل واحد منهم يتولى صياغة جزء مختلف من العقد. التعريفات، الالتزامات، الملكية الفكرية، الإنهاء، الحوكمة — كلها تُبنى في الوقت نفسه. قوالب منشأتك أولًا. والأنظمة السعودية دائمًا في الأساس.' },
        { name: 'Examiner',  role: 'يراجع المخاطر القانونية', desc: 'يقيّم كل عقد عبر عشرة أبعاد قانونية. يكشف الالتزامات الضعيفة، والحمايات الناقصة، والعبارات الغامضة. كل ملاحظة تكون مدعومة بإحالة إلى مادة محددة في النظام السعودي.' },
        { name: 'Regis',     role: 'يدقق الالتزام التنظيمي',  desc: 'يختبر العقد مقابل الأطر التنظيمية السعودية ذات العلاقة في وقت واحد، ويصدر تقرير التزام منظمًا لكل إطار، بدل ملخص عام لا يصلح لاتخاذ قرار.' },
        { name: 'Conductor', role: 'يوحد النتائج',            desc: 'يجمع ملاحظات المخاطر من Examiner وفجوات الالتزام من Regis في تقرير واحد مرتب حسب الأولوية. لا تضيع ملاحظة. لا يبقى تعارض دون معالجة. مخرج واحد واضح.' },
        { name: 'Sentinel',  role: 'يحمي قاعدة المعرفة',     desc: 'يقرأ كل وثيقة يرفعها فريقك، يصنفها، يلخصها، ويراقبها. يكشف التعارضات بين سياساتك الداخلية قبل أن تتحول إلى مشكلة حوكمة.' },
        { name: 'Herald',    role: 'يوجه المعرفة القانونية',  desc: 'يحول الملاحظة المختصرة إلى مذكرة قانونية مكتملة السياق، ثم يوجهها للشخص المناسب مع العقد، والنتائج، والإجراء المقترح. داخل المنصة وبالبريد الإلكتروني.' },
        { name: 'Counsel',  role: 'يبحث في الأنظمة السعودية',   desc: 'يجيب عن الأسئلة استنادًا إلى النص الفعلي للنظام — الأنظمة واللوائح السارية، مع الإسناد إلى المادة لا إلى ملخص عنها.' },
      ],
    },
    features: {
      eyebrow: 'المنصة',
      headline: ['مصممة لطريقة العمل', 'القانوني الفعلية في السعودية.'],
      cards: {
        assistant: {
          eyebrow: 'البحث القانوني',
          title: 'مساعد قانوني ذكي',
          desc: 'اسأل عن أي مسألة في الأنظمة السعودية. تحصل على إجابة مبنية على النص النظامي نفسه، مع الإحالة إلى المادة، لا إعادة صياغة من ملخص عام. بالعربية أو الإنجليزية.',
          demoQuestion: 'كم عدد ساعات العمل الأسبوعية حسب نظام العمل السعودي؟',
          demoAnswer: 'وفقًا للمادة 98 من نظام العمل، لا يجوز تشغيل العامل تشغيلًا فعليًا أكثر من ثماني ساعات في اليوم أو ثمانٍ وأربعين ساعة في الأسبوع.',
          demoArticle: 'المادة 98',
          demoSourcesPill: 'تم استخدام 6 مصادر قانونية',
        },
        review: {
          eyebrow: 'التحليل',
          title: 'مراجعة العقود وتحليل المخاطر',
          desc: 'ارفع أي عقد. أربعة وكلاء يراجعونه بالتوازي. تقرير واحد مرتب حسب الأولوية.',
          risks: [
            { label: 'حرج',    text: 'غياب شرط التحكيم' },
            { label: 'متوسط',  text: 'مدة الإشعار أقل من الحد المطلوب' },
            { label: 'منخفض', text: 'شرط السرية موجود' },
          ],
        },
        drafting: {
          eyebrow: 'الصياغة',
          title: 'صياغة العقود',
          desc: 'اكتب المطلوب بالعربية أو الإنجليزية. خمسة أقسام تُصاغ بالتوازي. عقد مكتمل خلال دقائق.',
          previewTitle: 'اتفاقية عدم إفصاح متبادلة',
          articles: ['المادة الأولى: التعريفات', 'المادة الثانية: التزامات السرية', 'المادة الثالثة: الإفصاحات المسموح بها'],
          more: '+ 6 مواد أخرى…',
        },
        kb: {
          eyebrow: 'المعرفة',
          title: 'قاعدة المعرفة',
          desc: 'سياسات منشأتك وقوالبها ترفع مرة واحدة، وتعمل في كل مكان داخل المنصة. Sentinel يراقب كل شيء تلقائيًا.',
          docs: [
            { name: 'سياسة الموارد البشرية 2025', status: 'نشط' },
            { name: 'مصفوفة الصلاحيات v3',         status: 'نشط' },
            { name: 'قالب اتفاقية شراكة',           status: 'نشط' },
          ],
        },
        herald: {
          eyebrow: 'التعاون',
          title: 'Herald',
          desc: 'اكتب ملاحظة مختصرة. Herald يحولها إلى إحاطة قانونية متكاملة، ثم يوجهها للزميل المناسب مع كل المرفقات.',
          demoLabel: 'إلى',
          demoInput: '@Ahmed — غير متأكد من البند 5',
          demoAttachment: 'تم إرفاق اتفاقية الشراكة',
          demoPreview: 'معاينة الرسالة ←',
        },
        library: {
          eyebrow: 'أرشيف المخرجات',
          title: 'المكتبة',
          desc: 'كل عقد أنتجته Bayenah لفريقك — محفوظ، قابل للبحث، وجاهز لإعادة الاستخدام. تنزيل بصيغة DOCX أو PDF أو نص. مراجعة مجددًا بنقرة واحدة. معرفتك المؤسسية لا تغادر معك.',
          entries: [
            { name: 'اتفاقية شراكة — أرامكو.pdf', status: 'مُصاغ',  date: 'يناير 2026' },
            { name: 'اتفاقية عدم إفصاح — مراجعة مورّد.pdf', status: 'مُراجَع', date: 'يناير 2026' },
            { name: 'عقد عمل.docx', status: 'مُصاغ',  date: 'ديسمبر 2025' },
          ],
          download: 'تنزيل',
        },
      },
    },
    compare: {
      eyebrow: 'لماذا ليس الذكاء الاصطناعي العام؟',
      headline: ['الذكاء العام يخمّن النظام السعودي.', 'Bayenah يعرفه.'],
      colGen: 'الذكاء الاصطناعي العام',
      colBay: 'Bayenah',
      rows: [
        { feature: 'المعرفة القانونية',  gen: '✗  تقديرات مبنية على بيانات عامة',          bay: '✓  أنظمة سعودية مع إحالات على مستوى المادة' },
        { feature: 'قوالب منشأتك',        gen: '✗  غير موجودة',                            bay: '✓  مكتبة تغذي كل مسودة' },
        { feature: 'المعالجة',           gen: '✗  نموذج واحد يعمل بالتسلسل',                bay: '✓  سبعة وكلاء يعملون بالتوازي' },
        { feature: 'سيادة البيانات',     gen: '✗  العقود تخرج خارج بيئة المنشأة',          bay: '✓  تبقى داخل بنيتك التحتية' },
        { feature: 'تعاون الفريق',       gen: '✗  غير متاح',                              bay: '✓  Herald للتوجيه والإحالة عبر @mention' },
        { feature: 'ذكاء الحوكمة',       gen: '✗  غير متاح',                              bay: '✓  Sentinel Guardian' },
        { feature: 'اللغة',              gen: '✗  الإنجليزية أولًا والعربية مترجمة',        bay: '✓  عربي أصيل وإنجليزي بنفس القوة' },
      ],
      quote: '«ChatGPT نموذج عام. Bayenah متخصص. لا تطلب من طبيب عام أن يجري عملية جراحية.»',
    },
    security: {
      eyebrow: 'الأمان',
      headline: 'بياناتك لا تخرج من سيطرتك.',
      sub: 'للفرق القانونية التي تتعامل مع مفاوضات حساسة، وصفقات استحواذ، وملفات مؤسسية سرية — تقدم Bayenah ثلاثة خيارات تشغيل.',
      options: [
        { tag: 'قياسي',  label: 'الخيار A', title: 'Bayenah Cloud',  body: 'تتم معالجة البيانات وتخزينها داخل المنطقة. متوافق مع متطلبات حماية البيانات الشخصية من خلال البنية، لا من خلال النصوص فقط.' },
        { tag: 'مؤسسي', label: 'الخيار B', title: 'Amazon Bedrock', body: 'تعمل Bayenah بالكامل داخل بيئة AWS الخاصة بمنشأتك. لا توجد أي قدرة لـ Bayenah على الاطلاع على بياناتك. سيادة كاملة.' },
        { tag: 'حكومي',  label: 'الخيار C', title: 'تشغيل خاص',     body: 'تشغيل داخل مقر المنشأة أو على سحابة خاصة للجهات الحكومية وشبه الحكومية والبيئات المصنفة.' },
      ],
      trust: [
        'صلاحيات وصول حسب الدور',
        'سجلات تدقيق كاملة لكل إجراء',
        'تشفير أثناء التخزين والنقل',
        'لا تُستخدم بياناتك لتدريب نماذج الذكاء الاصطناعي',
      ],
    },
    cta: {
      headline: ['فريقك القانوني جاهز.', 'Bayenah جاهزة.'],
      body: 'السؤال ليس هل يحتاج فريقك إلى ذلك. السؤال الحقيقي: كم ستستمر في دفع تكلفة التأخير؟',
      ctaPrimary: 'جرب مجانا',
      ctaFullAccess: 'طلب الوصول الكامل',
      ctaSecondary: 'تحدث معنا',
      fineprint: 'بدون أي التزام.',
    },
    footer: {
      tagline: 'ذكاء قانوني للمؤسسات السعودية.',
      platform: 'المنصة',
      security: 'الأمان',
      trust: 'الثقة',
      privacy: 'الخصوصية',
      terms: 'الشروط والأحكام',
      copyright: `© ${new Date().getFullYear()} Bayenah. جميع الحقوق محفوظة.`,
    },
  },
};

const SUPPORT_MAILTO = 'mailto:hello@bayenah.com';

// ─────────────────────────────────────────────────────────────────────
export default function LandingPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, setIsAuthModalOpen } = useAuth();
  const { language, setLanguage } = useLanguage();
  const [redirectFrom, setRedirectFrom] = useState(null);

  const t = useMemo(() => COPY[language] || COPY.en, [language]);
  const isAr = language === 'ar';

  // Cluster A (iter 2): tab-local idle lock support. When the idle
  // timer redirects this tab to `/?idle=1`, show a non-blocking
  // banner with a Resume CTA. On Resume, clear the lock flag and
  // navigate back to the page the user was on (stashed by the idle
  // timer; falls back to /chat). Supabase's still-valid session re-
  // hydrates with zero password re-entry.
  const showIdleBanner = (typeof window !== 'undefined') && new URLSearchParams(window.location.search).get('idle') === '1';
  const handleResume = useCallback(() => {
    let target = '/chat';
    try {
      const stashed = sessionStorage.getItem('bayenah_tab_lock_from');
      if (stashed) target = stashed;
      sessionStorage.removeItem('bayenah_tab_locked');
      sessionStorage.removeItem('bayenah_tab_lock_from');
    } catch (_e) { /* ignore */ }
    window.location.replace(target);
  }, []);

  useEffect(() => {
    if (location.state?.showAuth) {
      setIsAuthModalOpen(true);
      // Cluster E.1.c (Feb 2026): preserve BOTH pathname AND search
      // through the auth bounce. Today's `pathname`-only capture
      // drops `?clause=…` (and any future query param). Path slot
      // remains the matter id; query slot remains the sub-anchor —
      // both must survive. Hash is NOT preserved because Supabase
      // OAuth overloads `#access_token=…` and we do not want to
      // ever stamp an arbitrary user hash through the post-auth
      // navigate.
      const fromLoc = location.state?.from;
      const target = fromLoc?.pathname
        ? `${fromLoc.pathname}${fromLoc.search || ''}`
        : '/chat';
      setRedirectFrom(target);
    }
  }, [location.state, setIsAuthModalOpen]);

  useEffect(() => {
    if (isAuthenticated && redirectFrom) {
      navigate(redirectFrom, { replace: true });
      setRedirectFrom(null);
    }
  }, [isAuthenticated, redirectFrom, navigate]);

  return (
    <div
      data-testid="landing-page"
      dir={t.dir}
      lang={t.lang}
      className="w-full min-h-screen bg-[#0A0A0A] text-white"
    >
      {showIdleBanner ? (
        <div
          data-testid="idle-lock-banner"
          role="status"
          className="fixed top-0 inset-x-0 w-full px-4 py-3 flex flex-wrap items-center gap-3"
          style={{ background: '#1A1A1A', borderBottom: '1px solid #2A2A2A', zIndex: 100 }}
        >
          <div className="flex-1 min-w-[200px] text-sm">
            <span className="font-medium">
              {isAr ? 'تم قفل هذه الجلسة بسبب عدم النشاط.' : 'This tab was locked due to inactivity.'}
            </span>{' '}
            <span className="text-white/70">
              {isAr
                ? 'لا تزال جلستك صالحة على الأجهزة والتبويبات الأخرى.'
                : 'Your session is still valid on other tabs and devices.'}
            </span>
          </div>
          <button
            type="button"
            onClick={handleResume}
            data-testid="idle-lock-resume"
            className="h-8 px-3 text-xs rounded-md bg-white text-black font-medium"
          >
            {isAr ? 'استئناف' : 'Resume'}
          </button>
        </div>
      ) : null}
      <Nav
        t={t}
        isAr={isAr}
        language={language}
        setLanguage={setLanguage}
        onSignIn={() => {
          // Feb-07 2026 prod-bug fix: on an idle-locked tab the user
          // is still authenticated — Supabase tokens were never
          // invalidated, only React state was cleared and the Nav
          // moved to /?idle=1 with `bayenah_tab_locked=1`. Opening
          // the AuthModal here is a UX dead-end because AuthModal
          // auto-closes itself the moment `isAuthenticated` flips
          // back to true after Supabase rehydrates (which happens
          // on every page mount). Looked like "Sign in did nothing"
          // to the user. Route the click through `handleResume`
          // (clear the lock + navigate back to the stashed path)
          // when we know we're on the locked surface.
          if (showIdleBanner) {
            handleResume();
            return;
          }
          setIsAuthModalOpen(true);
        }}
        topOffset={showIdleBanner ? 48 : 0}
      />
      <Hero t={t} isAr={isAr} />
      <Truth t={t} isAr={isAr} />
      <Engine t={t} isAr={isAr} />
      <Features t={t} isAr={isAr} />
      <Compare t={t} isAr={isAr} />
      <Security t={t} isAr={isAr} />
      <FinalCTA t={t} isAr={isAr} />
      <Footer t={t} isAr={isAr} />
    </div>
  );
}

// ── Nav ─────────────────────────────────────────────────────────────
function Nav({ t, isAr, language, setLanguage, onSignIn, topOffset = 0 }) {
  return (
    <nav
      data-testid="landing-nav"
      dir="ltr"
      className="absolute inset-x-0 z-30 px-4 sm:px-10 py-4 sm:py-5 flex items-center justify-between gap-2"
      // Feb-07 2026 prod-bug fix: when the idle-lock banner is on
      // screen, it occupies the top 48 px with a solid background +
      // z-index 100. The previous `top-0` placement meant the banner
      // sat directly on top of (and click-intercepted) this Nav,
      // hiding the Sign-in button completely. Offset the Nav by the
      // banner's height so both surfaces are interactive.
      style={{ top: topOffset }}
      aria-label="Primary"
    >
      <Link to="/" className="flex items-center gap-2 sm:gap-3 group min-w-0" data-testid="landing-nav-brand" aria-label="Bayenah — Home">
        <img
          src="/brand/mark-white.png"
          alt=""
          aria-hidden="true"
          style={{ height: '28px', width: '28px', objectFit: 'contain' }}
          className="sm:h-[34px] sm:w-[34px] shrink-0"
          draggable={false}
        />
        {!isAr ? (
          <div className="flex items-baseline min-w-0" data-testid="landing-nav-wordmark-en">
            <span className="text-white font-serif" style={{ fontSize: '14px', fontWeight: 600, letterSpacing: '-0.005em' }}>
              Bayenah
            </span>
            {/* Tagline hidden on mobile (<sm) — at 375px the brand,
                EN/AR pill, and CTA together overflow if the tagline is
                visible, which caused the language toggle to be clipped
                under the CTA (May-24 mobile audit, item #6). */}
            <span className="hidden sm:inline" style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)', letterSpacing: '0.06em', marginLeft: '8px' }}>
              Saudi Legal AI
            </span>
          </div>
        ) : (
          <div dir="rtl" className="flex items-baseline min-w-0" data-testid="landing-nav-wordmark-ar" style={{ fontFamily: 'var(--font-sans-ar)' }}>
            <span className="text-white" style={{ fontSize: '14px', fontWeight: 600 }}>بَيِّنَة</span>
            <span className="hidden sm:inline" style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)', letterSpacing: '0.06em', marginRight: '8px' }}>
              الذكاء القانوني السعودي
            </span>
          </div>
        )}
      </Link>

      <div className="hidden md:flex items-center gap-8 text-[14px] text-white/75">
        <a href="#engine"   className="hover:text-white transition" data-testid="landing-nav-platform">{t.nav.platform}</a>
        <a href="#security" className="hover:text-white transition" data-testid="landing-nav-security">{t.nav.security}</a>
        <Link to="/trust"   className="hover:text-white transition" data-testid="landing-nav-trust">{t.nav.trust}</Link>
      </div>

      <div className="flex items-center gap-2 sm:gap-3 shrink-0">
        <div className="inline-flex items-center rounded-full border border-white/25 overflow-hidden shrink-0" data-testid="landing-lang-toggle" role="group" aria-label="Language" style={{ height: '30px' }}>
          <button
            type="button" onClick={() => setLanguage('en')} data-testid="landing-lang-en"
            aria-pressed={language === 'en'}
            className={cn('text-[12px] font-medium px-3 transition',
              language === 'en' ? 'bg-white text-[#0A0A0A]' : 'text-white/60 hover:text-white')}
            style={{ height: '100%', letterSpacing: '0.06em' }}
          >EN</button>
          <span className="text-white/25 text-[11px]" aria-hidden="true">|</span>
          <button
            type="button" onClick={() => setLanguage('ar')} data-testid="landing-lang-ar"
            aria-pressed={language === 'ar'}
            className={cn('text-[12px] font-medium px-3 transition',
              language === 'ar' ? 'bg-white text-[#0A0A0A]' : 'text-white/60 hover:text-white')}
            style={{ height: '100%', fontFamily: 'var(--font-sans-ar)' }}
          >AR</button>
        </div>

        {/* Sign-in link — now visible on all viewports so existing users
            have a clear entry from the landing page on mobile (May-24
            audit, item #9). */}
        <button type="button" onClick={onSignIn} className="text-[12px] sm:text-[13px] text-white/75 hover:text-white transition shrink-0" data-testid="landing-signin">
          {t.nav.signin}
        </button>
        <Link
          to="/try"
          className="inline-flex items-center gap-1.5 rounded-full bg-white text-[#0A0A0A] text-[12px] sm:text-[13px] font-medium px-3 sm:px-4 py-1.5 sm:py-2 hover:bg-white/90 transition shrink-0 whitespace-nowrap"
          data-testid="landing-request-demo-nav"
        >
          {t.nav.requestDemo}
        </Link>
      </div>
    </nav>
  );
}

// ── Section 1: Hero ─────────────────────────────────────────────────
function Hero({ t, isAr }) {
  const scrollToEngine = () => {
    document.getElementById('engine')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section
      id="hero"
      data-testid="landing-hero"
      className="relative overflow-hidden bg-[#0A0A0A]"
      style={{ minHeight: '680px', height: '100vh', maxHeight: '960px' }}
    >
      {/* Subtle grid background — replaces hero photograph */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        data-testid="landing-hero-grid"
        style={{
          zIndex: 0,
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)',
          backgroundSize: '64px 64px',
        }}
      />

      <div className="relative h-full w-full" style={{ zIndex: 2 }} dir="ltr">
        <div className="max-w-[1440px] mx-auto h-full px-6 sm:px-10 lg:px-14 flex items-center">
          <div className="w-full grid lg:grid-cols-[minmax(0,1fr)_760px] gap-10 items-center">
            <div
              dir={t.dir}
              className={cn(
                'flex flex-col pt-28 lg:pt-0',
                isAr ? 'items-end text-right' : 'items-start text-left',
              )}
            >
              <div
                data-testid="landing-hero-eyebrow"
                style={{
                  fontSize: '12px',
                  color: 'rgba(255,255,255,0.6)',
                  letterSpacing: isAr ? '0.04em' : '0.22em',
                  textTransform: isAr ? 'none' : 'uppercase',
                  marginBottom: '20px',
                }}
              >
                {t.hero.eyebrow}
              </div>

              <h1
                data-testid="landing-hero-headline"
                className="text-white font-serif"
                style={{
                  fontSize: 'clamp(40px, 6vw, 80px)',
                  fontWeight: 600,
                  lineHeight: 1.05,
                  letterSpacing: isAr ? '0' : '-0.025em',
                  marginBottom: '24px',
                  color: '#FFFFFF',
                }}
              >
                {t.hero.headline[0]}<br />{t.hero.headline[1]}
              </h1>

              <p
                data-testid="landing-hero-sub"
                style={{
                  fontSize: 'clamp(15px, 1.4vw, 19px)',
                  color: 'rgba(255,255,255,0.8)',
                  lineHeight: 1.55,
                  maxWidth: '560px',
                  marginBottom: '32px',
                  fontWeight: 400,
                }}
              >
                {t.hero.sub}
              </p>

              <div className={cn('flex flex-wrap items-center gap-3', isAr && 'flex-row-reverse')}>
                <Link
                  to="/try"
                  className="inline-flex items-center justify-center rounded-full bg-white text-[#0A0A0A] hover:bg-white/90 transition"
                  data-testid="landing-cta-start-free"
                  style={{ fontSize: '14px', fontWeight: 500, padding: '12px 22px' }}
                >
                  {t.hero.ctaPrimary}
                </Link>
                <Link
                  to="/waitlist"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-white/35 text-white hover:bg-white/10 hover:border-white/55 transition"
                  data-testid="landing-cta-request-full-access"
                  style={{ fontSize: '14px', fontWeight: 500, padding: '12px 22px' }}
                >
                  {t.hero.ctaFullAccess}
                </Link>
                <button
                  type="button"
                  onClick={scrollToEngine}
                  className="inline-flex items-center justify-center gap-2 text-white/70 hover:text-white transition"
                  data-testid="landing-cta-watch-demo"
                  style={{ fontSize: '14px', fontWeight: 500, padding: '12px 8px' }}
                >
                  {t.hero.ctaSecondary}
                </button>
              </div>
            </div>

            <div
              className="hidden lg:flex flex-col items-center justify-center"
              data-testid="landing-hero-globe-wrap"
              style={{ width: '782px', height: '782px', flexShrink: 0 }}
            >
              <InteractiveGlobe size={782} />
              <div style={{ marginTop: 12, fontSize: 10, color: 'rgba(255,255,255,0.2)', letterSpacing: '0.08em' }}>
                {t.hero.globeHint}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Section 2: Truth ────────────────────────────────────────────────
function Truth({ t, isAr }) {
  return (
    <section id="truth" data-testid="landing-truth" className="bg-[#0A0A0A] text-white border-t border-white/5">
      <div className="max-w-6xl mx-auto px-6 py-28">
        <Eyebrow isAr={isAr}>{t.truth.eyebrow}</Eyebrow>
        <h2 className="font-serif" style={{ fontSize: 'clamp(28px, 4vw, 46px)', fontWeight: 500, lineHeight: 1.1, color: '#FFFFFF', marginTop: 12, maxWidth: 880 }}>
          {t.truth.headline}
        </h2>
        <p className="text-white/65 leading-relaxed mt-6" style={{ fontSize: '15.5px', maxWidth: 760 }}>
          {t.truth.body}
        </p>

        <div className="mt-14 grid md:grid-cols-3 gap-4">
          {t.truth.cards.map((c, i) => (
            <div
              key={i}
              data-testid={`landing-truth-card-${i}`}
              style={{ background: '#0d0d0d', border: '1px solid #1a1a1a', borderRadius: 12, padding: 24, minHeight: 220 }}
            >
              <Eyebrow isAr={isAr} muted>{c.label}</Eyebrow>
              <h3 className="font-serif mt-2" style={{ fontSize: 20, color: '#FFFFFF', fontWeight: 500, lineHeight: 1.25 }}>
                {c.title}
              </h3>
              <p className="text-white/55 mt-3" style={{ fontSize: 13, lineHeight: 1.7 }}>
                {c.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Section 3: Engine ───────────────────────────────────────────────
function Engine({ t, isAr }) {
  return (
    <section id="engine" data-testid="landing-engine" className="bg-[#0A0A0A] text-white border-t border-white/5">
      {/* Section divider — subtle ambient Bayenah mark */}
      <div
        data-testid="landing-divider-mark"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '12px 0',
          borderTop: '1px solid #0d0d0d',
        }}
      >
        <img
          src="/brand/mark-white.png"
          alt=""
          aria-hidden="true"
          style={{ height: '16px', width: '16px', opacity: 0.12, objectFit: 'contain' }}
        />
      </div>
      <div className="max-w-6xl mx-auto px-6 py-28">
        <Eyebrow isAr={isAr}>{t.engine.eyebrow}</Eyebrow>
        <h2 className="font-serif mt-3" style={{ fontSize: 'clamp(28px, 4vw, 46px)', fontWeight: 500, lineHeight: 1.1 }}>
          {t.engine.headline}
        </h2>
        <p className="text-white/55 mt-5 leading-relaxed" style={{ fontSize: 14.5, maxWidth: 720 }}>
          {t.engine.sub}
        </p>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-px" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.04)' }} data-testid="landing-engine-grid">
          {t.engine.agents.map((a, i) => (
            <div
              key={a.name}
              data-testid={`landing-engine-agent-${i}`}
              style={{ background: '#0A0A0A', padding: 28, minHeight: 220 }}
            >
              <div
                style={{
                  fontSize: 11,
                  color: 'rgba(255,255,255,0.35)',
                  letterSpacing: isAr ? '0' : '0.06em',
                  textTransform: isAr ? 'none' : 'uppercase',
                  marginBottom: 6,
                }}
              >
                {a.role}
              </div>
              <div style={{ fontSize: 15, color: '#FFFFFF', fontWeight: 600, fontFamily: '"DM Sans", sans-serif' }}>
                {a.name}
              </div>
              <div style={{ height: 1, background: '#111111', margin: '14px 0 14px' }} />
              <p style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.45)', lineHeight: 1.7 }}>
                {a.desc}
              </p>
            </div>
          ))}
        </div>

        <p
          className="mt-12 text-center"
          style={{
            fontFamily: 'Georgia, serif',
            fontStyle: 'italic',
            fontSize: 14,
            color: 'rgba(255,255,255,0.30)',
            maxWidth: 720,
            margin: '48px auto 0',
            lineHeight: 1.7,
          }}
          data-testid="landing-engine-closing"
        >
          {t.engine.closing}
        </p>
      </div>
    </section>
  );
}

// ── Section 4: Features (bento) ─────────────────────────────────────
function Features({ t, isAr }) {
  const cardBase = {
    background: '#0d0d0d',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 12,
    padding: 28,
    overflow: 'hidden',
  };
  const eyebrowStyle = {
    fontSize: 11,
    color: 'rgba(255,255,255,0.35)',
    letterSpacing: isAr ? '0' : '0.06em',
    textTransform: isAr ? 'none' : 'uppercase',
    marginBottom: 8,
  };
  const titleStyle = { fontSize: 20, color: '#FFFFFF', fontWeight: 500, marginBottom: 8 };
  const copyStyle = { fontSize: 13, color: 'rgba(255,255,255,0.55)', lineHeight: 1.7 };
  const C = t.features.cards;

  return (
    <section id="features" data-testid="landing-features" style={{ background: '#0A0A0A', padding: '96px 24px', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ marginBottom: 48 }}>
          <Eyebrow isAr={isAr}>{t.features.eyebrow}</Eyebrow>
          <h2 className="font-serif" style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 500, color: '#FFFFFF', lineHeight: 1.15, marginTop: 12, marginBottom: 12 }}>
            {t.features.headline[0]}<br />{t.features.headline[1]}
          </h2>
        </div>

        <div
          className="bayenah-bento"
          data-testid="landing-bento"
          style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 16 }}
        >
          {/* Card 1 — Assistant */}
          <div data-testid="bento-card-assistant" style={{ ...cardBase, gridColumn: '1 / 8', minHeight: 320, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <div>
              <div style={eyebrowStyle}>{C.assistant.eyebrow}</div>
              <h3 style={titleStyle}>{C.assistant.title}</h3>
              <p style={{ ...copyStyle, maxWidth: 420 }}>{C.assistant.desc}</p>
            </div>
            <div style={{ background: '#111', borderRadius: 8, padding: 14, marginTop: 20, border: '1px solid rgba(255,255,255,0.06)' }}>
              <div style={{ background: '#1A1A1A', borderRadius: 8, padding: '10px 14px', marginBottom: 8, fontSize: 11.5, color: 'rgba(255,255,255,0.55)' }}>
                {C.assistant.demoQuestion}
              </div>
              <div style={{ background: '#161616', borderRadius: 8, padding: '10px 14px', fontSize: 11.5, color: 'rgba(255,255,255,0.75)', lineHeight: 1.7 }}>
                {C.assistant.demoAnswer}
              </div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 10, padding: '3px 10px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 9999, fontSize: 10, color: 'rgba(255,255,255,0.50)' }}>
                {C.assistant.demoSourcesPill}
              </div>
            </div>
          </div>

          {/* Card 2 — Review */}
          <div data-testid="bento-card-review" style={{ ...cardBase, gridColumn: '8 / 13', minHeight: 320 }}>
            <div style={eyebrowStyle}>{C.review.eyebrow}</div>
            <h3 style={titleStyle}>{C.review.title}</h3>
            <p style={{ ...copyStyle, marginBottom: 20 }}>{C.review.desc}</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {C.review.risks.map((r, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#111', borderRadius: 6, padding: '8px 12px', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'rgba(255,255,255,0.55)', flexShrink: 0 }} />
                  <span style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.75)', flex: 1 }}>{r.text}</span>
                  <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.5)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    {r.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Card 3 — Drafting */}
          <div data-testid="bento-card-drafting" style={{ ...cardBase, gridColumn: '1 / 6', minHeight: 300 }}>
            <div style={eyebrowStyle}>{C.drafting.eyebrow}</div>
            <h3 style={titleStyle}>{C.drafting.title}</h3>
            <p style={{ ...copyStyle, marginBottom: 20 }}>{C.drafting.desc}</p>
            <div style={{ background: '#FFFFFF', borderRadius: 6, padding: '12px 14px', fontSize: 11, color: '#1B1B1A', lineHeight: 1.8 }}>
              <div style={{ fontWeight: 600, fontSize: 11.5, marginBottom: 6, textAlign: 'center' }}>{C.drafting.previewTitle}</div>
              {C.drafting.articles.map((a, i) => (
                <div key={i} style={{ color: '#666', marginBottom: 4 }}>{a}</div>
              ))}
              <div style={{ color: '#999', fontSize: 10 }}>{C.drafting.more}</div>
            </div>
          </div>

          {/* Card 4 — Knowledge Base */}
          <div data-testid="bento-card-kb" style={{ ...cardBase, gridColumn: '6 / 13', minHeight: 300 }}>
            <div style={eyebrowStyle}>{C.kb.eyebrow}</div>
            <h3 style={titleStyle}>{C.kb.title}</h3>
            <p style={{ ...copyStyle, marginBottom: 20 }}>{C.kb.desc}</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {C.kb.docs.map((d, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#111', borderRadius: 6, padding: '10px 12px', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)' }}>{d.name}</span>
                  <span style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.25)', letterSpacing: '0.06em' }}>{d.status}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Card 5 — Library (replaces Herald) */}
          <div
            data-testid="bento-card-library"
            style={{ ...cardBase, gridColumn: '1 / 13', minHeight: 220, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 40, flexWrap: 'wrap' }}
          >
            <div style={{ maxWidth: 460 }}>
              <div style={eyebrowStyle}>{C.library.eyebrow}</div>
              <h3 style={titleStyle}>{C.library.title}</h3>
              <p style={copyStyle}>{C.library.desc}</p>
            </div>
            <div style={{ flex: 1, minWidth: 320, maxWidth: 540 }}>
              <div style={{ background: '#111', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, padding: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {C.library.entries.map((e, i) => (
                  <div
                    key={i}
                    data-testid={`bento-library-row-${i}`}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: isAr ? 'auto auto 1fr auto' : '1fr auto auto auto',
                      alignItems: 'center',
                      gap: 12,
                      background: '#0A0A0A',
                      border: '1px solid rgba(255,255,255,0.06)',
                      borderRadius: 6,
                      padding: '10px 12px',
                    }}
                  >
                    <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {e.name}
                    </span>
                    <span style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.55)', padding: '2px 7px', borderRadius: 4, border: '1px solid rgba(255,255,255,0.10)', whiteSpace: 'nowrap' }}>
                      {e.status}
                    </span>
                    <span style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.04em', whiteSpace: 'nowrap', textAlign: isAr ? 'right' : 'left' }}>
                      {e.date}
                    </span>
                    <button
                      type="button"
                      data-testid={`bento-library-download-${i}`}
                      style={{ fontSize: 10.5, padding: '3px 9px', borderRadius: 4, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.85)', cursor: 'pointer', whiteSpace: 'nowrap' }}
                    >
                      {C.library.download}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 720px) {
          .bayenah-bento > div { grid-column: 1 / -1 !important; }
        }
      `}</style>
    </section>
  );
}

// ── Section 5: Compare ──────────────────────────────────────────────
function Compare({ t, isAr }) {
  return (
    <section id="compare" data-testid="landing-compare" className="relative bg-[#0A0A0A] text-white border-t border-white/5 overflow-hidden">
      <img
        src="/brand/mark-white.png"
        alt=""
        aria-hidden="true"
        data-testid="landing-compare-watermark"
        style={{
          position: 'absolute',
          bottom: '40px',
          right: '48px',
          height: '120px',
          width: '120px',
          opacity: 0.03,
          objectFit: 'contain',
          pointerEvents: 'none',
        }}
      />
      <div className="max-w-5xl mx-auto px-6 py-28 relative">
        <Eyebrow isAr={isAr}>{t.compare.eyebrow}</Eyebrow>
        <h2 className="font-serif mt-3" style={{ fontSize: 'clamp(26px, 3.6vw, 40px)', fontWeight: 500, lineHeight: 1.15 }}>
          {t.compare.headline[0]}<br />{t.compare.headline[1]}
        </h2>

        <div className="mt-14 overflow-hidden rounded-xl" style={{ border: '1px solid rgba(255,255,255,0.06)' }} data-testid="landing-compare-table">
          <div className="grid" style={{ gridTemplateColumns: isAr ? '1fr 1fr 1.2fr' : '1.2fr 1fr 1fr' }}>
            {/* Header row */}
            <div style={{ background: 'rgba(255,255,255,0.025)', padding: '16px 20px', fontSize: 11, color: 'rgba(255,255,255,0.5)', letterSpacing: isAr ? '0' : '0.08em', textTransform: isAr ? 'none' : 'uppercase' }}>
              {isAr ? 'الميزة' : 'Feature'}
            </div>
            <div style={{ background: 'rgba(255,255,255,0.015)', padding: '16px 20px', fontSize: 11, color: 'rgba(255,255,255,0.5)', letterSpacing: isAr ? '0' : '0.08em', textTransform: isAr ? 'none' : 'uppercase', fontStyle: 'italic' }}>
              {t.compare.colGen}
            </div>
            <div style={{ background: 'rgba(255,255,255,0.04)', padding: '16px 20px', fontSize: 11, color: '#FFFFFF', letterSpacing: isAr ? '0' : '0.08em', textTransform: isAr ? 'none' : 'uppercase', fontWeight: 600, borderInlineStart: '1px solid rgba(255,255,255,0.08)' }}>
              {t.compare.colBay}
            </div>
            {/* Body rows */}
            {t.compare.rows.map((r, i) => (
              <React.Fragment key={i}>
                <div style={{ background: 'rgba(255,255,255,0.025)', padding: '16px 20px', fontSize: 13, color: '#FFFFFF', fontWeight: 500, borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                  {r.feature}
                </div>
                <div style={{ background: 'rgba(255,255,255,0.015)', padding: '16px 20px', fontSize: 13, color: 'rgba(255,255,255,0.40)', fontStyle: 'italic', borderTop: '1px solid rgba(255,255,255,0.04)', lineHeight: 1.55 }}>
                  {r.gen}
                </div>
                <div style={{ background: 'rgba(255,255,255,0.04)', padding: '16px 20px', fontSize: 13, color: '#FFFFFF', borderTop: '1px solid rgba(255,255,255,0.04)', borderInlineStart: '1px solid rgba(255,255,255,0.08)', lineHeight: 1.55 }}>
                  {r.bay}
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>

        <p
          className="mt-10 text-center"
          style={{
            fontFamily: 'Georgia, serif',
            fontStyle: 'italic',
            fontSize: 14,
            color: 'rgba(255,255,255,0.35)',
            maxWidth: 720,
            margin: '40px auto 0',
            lineHeight: 1.7,
          }}
          data-testid="landing-compare-quote"
        >
          {t.compare.quote}
        </p>
      </div>
    </section>
  );
}

// ── Section 6: Security ─────────────────────────────────────────────
function Security({ t, isAr }) {
  return (
    <section id="security" data-testid="landing-security" className="bg-[#0A0A0A] text-white border-t border-white/5">
      <div className="max-w-6xl mx-auto px-6 py-28">
        <Eyebrow isAr={isAr}>{t.security.eyebrow}</Eyebrow>
        <h2 className="font-serif mt-3" style={{ fontSize: 'clamp(26px, 3.4vw, 38px)', fontWeight: 500, lineHeight: 1.15 }}>
          {t.security.headline}
        </h2>
        <p className="text-white/55 mt-5 leading-relaxed" style={{ fontSize: 14.5, maxWidth: 720 }}>
          {t.security.sub}
        </p>

        <div className="mt-12 grid md:grid-cols-3 gap-4" data-testid="landing-security-options">
          {t.security.options.map((o, i) => (
            <div
              key={i}
              data-testid={`landing-security-option-${i}`}
              style={{ background: '#0d0d0d', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: 24, minHeight: 220 }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                <span style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.5)', letterSpacing: isAr ? '0' : '0.06em', textTransform: isAr ? 'none' : 'uppercase', padding: '2px 8px', borderRadius: 4, border: '1px solid rgba(255,255,255,0.08)' }}>
                  {o.tag}
                </span>
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>{o.label}</span>
              </div>
              <h3 className="font-serif" style={{ fontSize: 19, color: '#FFFFFF', fontWeight: 500, marginBottom: 10 }}>
                {o.title}
              </h3>
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', lineHeight: 1.7 }}>
                {o.body}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3" data-testid="landing-trust-row">
          {t.security.trust.map((line, i) => (
            <span key={i} style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.5)' }}>
              <span style={{ color: 'rgba(255,255,255,0.85)', marginInlineEnd: 6 }}>✓</span>
              {line}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Section 7: Final CTA ────────────────────────────────────────────
function FinalCTA({ t }) {
  return (
    <section data-testid="landing-final-cta" className="bg-[#0A0A0A] text-white border-t border-white/5 relative overflow-hidden">
      {/* Large subtle Bayenah mark watermark behind headline */}
      <img
        src="/brand/mark-white.png"
        alt=""
        aria-hidden="true"
        data-testid="landing-final-cta-watermark"
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%,-50%)',
          height: '320px',
          width: '320px',
          opacity: 0.025,
          objectFit: 'contain',
          pointerEvents: 'none',
        }}
      />
      {/* Subtle grid background */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(to right, rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.04) 1px, transparent 1px)',
          backgroundSize: '64px 64px',
          maskImage: 'radial-gradient(circle at center, rgba(0,0,0,0.6) 0%, transparent 70%)',
          WebkitMaskImage: 'radial-gradient(circle at center, rgba(0,0,0,0.6) 0%, transparent 70%)',
        }}
      />
      <div className="relative max-w-4xl mx-auto px-6 py-28 text-center space-y-6">
        <h2 className="font-serif" style={{ fontSize: 'clamp(28px, 3.6vw, 42px)', fontWeight: 500, lineHeight: 1.15 }}>
          {t.cta.headline[0]}<br />{t.cta.headline[1]}
        </h2>
        <p className="text-white/65 leading-relaxed max-w-2xl mx-auto" style={{ fontSize: 15 }}>
          {t.cta.body}
        </p>
        <div className="flex items-center justify-center gap-3 pt-2 flex-wrap">
          <Link
            to="/try"
            data-testid="landing-final-start-free"
            className="inline-flex items-center justify-center rounded-full bg-white text-[#0A0A0A] hover:bg-white/90 transition"
            style={{ fontSize: 13.5, padding: '10px 22px' }}
          >
            {t.cta.ctaPrimary}
          </Link>
          <Link
            to="/waitlist"
            data-testid="landing-final-request-full-access"
            className="inline-flex items-center justify-center rounded-full border border-white/25 text-white hover:border-white/50 transition"
            style={{ fontSize: 13.5, padding: '10px 22px' }}
          >
            {t.cta.ctaFullAccess}
          </Link>
          <a
            href={SUPPORT_MAILTO}
            data-testid="landing-final-talk"
            className="inline-flex items-center justify-center text-white/70 hover:text-white transition"
            style={{ fontSize: 13.5, padding: '10px 8px' }}
          >
            {t.cta.ctaSecondary}
          </a>
        </div>
        <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.40)' }}>{t.cta.fineprint}</p>
      </div>
    </section>
  );
}

// ── Footer ──────────────────────────────────────────────────────────
function Footer({ t, isAr }) {
  return (
    <footer data-testid="landing-footer" className="bg-[#0A0A0A] text-white/55 border-t border-white/5">
      <div className="max-w-6xl mx-auto px-6 py-12 grid sm:grid-cols-[1fr_auto] gap-8 items-end">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="font-serif text-white text-[14px]">{isAr ? 'بَيِّنَة' : 'Bayenah'}</span>
            <span className="text-white/45 tracking-[0.2em] text-[11.5px] uppercase">{isAr ? 'BAYENAH' : 'بَيِّنَة'}</span>
          </div>
          <p className="text-[12px] text-white/45">{t.footer.tagline}</p>
        </div>
        <nav className={cn('flex flex-wrap items-center gap-6 text-[12.5px]', isAr ? 'justify-start' : 'justify-end')}>
          <a href="#engine"   className="hover:text-white">{t.footer.platform}</a>
          <a href="#security" className="hover:text-white">{t.footer.security}</a>
          <Link to="/trust"   className="hover:text-white">{t.footer.trust}</Link>
          <Link to="/privacy" className="hover:text-white" data-testid="landing-footer-privacy">{t.footer.privacy}</Link>
          <Link to="/terms"   className="hover:text-white" data-testid="landing-footer-terms">{t.footer.terms}</Link>
        </nav>
      </div>
      <div className="border-t border-white/5">
        <div className="max-w-6xl mx-auto px-6 py-6 flex flex-wrap items-center justify-between gap-3 text-[11.5px] text-white/40">
          <a href="mailto:hello@bayenah.com" className="hover:text-white">hello@bayenah.com</a>
          <span>{t.footer.copyright}</span>
        </div>
      </div>
    </footer>
  );
}

// ── Shared: Eyebrow text ────────────────────────────────────────────
function Eyebrow({ children, isAr, muted = false }) {
  return (
    <div
      style={{
        fontSize: 11,
        color: muted ? 'rgba(255,255,255,0.30)' : 'rgba(255,255,255,0.55)',
        letterSpacing: isAr ? '0' : '0.18em',
        textTransform: isAr ? 'none' : 'uppercase',
      }}
    >
      {children}
    </div>
  );
}
